/**
 * The lead record page's body — `lead-detail.html` ported.
 *
 * Split out of `LeadDetailPage.jsx` because that file is already the layout,
 * the path/status logic and the header actions; this one is everything that
 * goes *inside* the three columns.
 *
 * Three different levels of "real" show up on this one page, and each panel
 * says which it is in its own comment rather than leaving the reader to guess:
 *
 * - **Live data** — score, engagement, address, qualification, SLA, the
 *   duplicate-match card, and now the Activity tab and the Open Activities
 *   related list — all come straight off the API (`GET /leads/{id}`, or
 *   `GET /activities?lead_id=…` for the timeline itself).
 * - **Illustrative only** — Campaign History, Lead History, News and the two
 *   right-column suggestion cards are exactly the prototype's own hardcoded
 *   rows. Nothing backs a campaign-membership list or a news feed yet, so
 *   inventing per-lead data for them would be less honest than the
 *   prototype's own placeholder content, not more.
 */
import { Card, Avatar, EmptyState } from '../../components/common/index.jsx';
import { InlineCell } from './LeadViews.jsx';
import { STATUS_PILL } from './leadShape.js';
import { money, number, date, dateTime, relativeTime } from '../../utils/formatters.js';
import { useActivities } from '../activities/useActivities.js';

/* -------------------------------------------------------------------------- */
/* Predictive lead score                                                      */
/* -------------------------------------------------------------------------- */

const BAND_COLOR = { Hot: 'var(--rose)', Warm: 'var(--amber)', Cold: 'var(--sky)' };
const CIRCUMFERENCE = 283; // 2 * pi * r45, matching the prototype's SVG arc

/**
 * The gauge and the "why this score" factor list.
 *
 * Reads `lead.scoreBreakdown.contributions` — the model's own snapshot of
 * which rules fired, taken when the score was last computed — rather than
 * recomputing anything client-side. A number next to an explanation that no
 * longer matches the rules that produced it is worse than no explanation.
 */
export function ScoreCard({ lead }) {
  const breakdown = lead.scoreBreakdown;
  const contributions = breakdown?.contributions ?? [];
  const widest = Math.max(1, ...contributions.map((c) => Math.abs(c.points)));
  const band = breakdown?.band ?? lead.rating;
  const offset = CIRCUMFERENCE - (CIRCUMFERENCE * (lead.score ?? 0)) / 100;
  const subtitle = breakdown?.computed_at
    ? `Model refreshed ${relativeTime(breakdown.computed_at)}`
    : lead.score == null
      ? 'Not yet scored'
      : 'No contribution snapshot on file';

  return (
    <Card
      className="mb-3"
      style={{ borderColor: 'var(--brand-200)' }}
      headStyle={{ background: 'linear-gradient(120deg,var(--brand-50),#fff)' }}
      title={<span className="d-flex align-items-center gap-2"><i className="bi bi-stars" aria-hidden="true" />Predictive Lead Score</span>}
      subtitle={subtitle}
    >
      <div className="score-gauge mb-2">
        <svg width="104" height="104" viewBox="0 0 104 104">
          <circle cx="52" cy="52" r="45" fill="none" stroke="#eff1f8" strokeWidth="9" />
          <circle
            cx="52" cy="52" r="45" fill="none" stroke="url(#sg)" strokeWidth="9"
            strokeLinecap="round" strokeDasharray={CIRCUMFERENCE} strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 1.1s cubic-bezier(.4,0,.2,1)' }}
          />
          <defs>
            <linearGradient id="sg" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#6366f1" />
              <stop offset="100%" stopColor="#ec4899" />
            </linearGradient>
          </defs>
        </svg>
        <div className="sg-val">
          <div className="t-2xl fw-750 lh-1 tabular">{lead.score ?? '—'}</div>
          <div className="t-2xs fw-700 tracking-wide text-uppercase" style={{ color: BAND_COLOR[band] }}>{band ?? '—'}</div>
        </div>
      </div>

      <div className="t-2xs fw-700 tracking-wide text-uppercase ink-3 mb-1">Why this score</div>
      {contributions.length ? contributions.map((c) => (
        <div key={c.label} className="score-factor">
          <span className="fw-650" style={{ color: c.points < 0 ? '#be123c' : '#047857', width: 26 }}>
            {c.points > 0 ? '+' : ''}{c.points}
          </span>
          <span className="ink-2 flex-grow-1 text-truncate" title={`${c.label} (${c.group})`}>{c.label}</span>
          <span className="sf-bar" style={{ maxWidth: 44 }}>
            <span style={{ width: `${Math.round((Math.abs(c.points) / widest) * 100)}%`, background: c.points < 0 ? '#f43f5e' : '#10b981' }} />
          </span>
        </div>
      )) : (
        <div className="t-xs ink-4">
          {breakdown ? 'No scoring rules matched this lead.' : 'This lead has not been scored since the model last changed.'}
        </div>
      )}

      <div className="t-2xs ink-4 mt-2 d-flex gap-1 align-items-start">
        <i className="bi bi-info-circle mt-1" aria-hidden="true" />
        <span>Scores are explanatory only and never overwrite the manual Rating field.</span>
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Related lists                                                             */
/* -------------------------------------------------------------------------- */

function RelatedList({ title, count, icon, color, rows, empty }) {
  return (
    <div className="rel-list fade-up">
      <div className="rel-head">
        <div className="ri" style={{ background: color }}><i className={`bi ${icon}`} aria-hidden="true" /></div>
        <div className="min-w-0"><div className="rt text-truncate">{title}</div><div className="rc">{count} item{count === 1 ? '' : 's'}</div></div>
      </div>
      {rows.length
        ? rows.map((r, i) => (
          <div key={i} className="rel-row">
            <div className="t-sm fw-600 text-truncate">{r.title}</div>
            <div className="t-2xs ink-4">{r.meta}</div>
          </div>
        ))
        : <div className="rel-row t-xs ink-4">{empty}</div>}
    </div>
  );
}

/**
 * Campaign History and Notes read real fields; Open Activities and Lead
 * History are the prototype's own illustrative rows — see the file banner.
 */
export function RelatedLists({ lead }) {
  const notes = lead.notes;

  /* Same params as `ActivityTab`'s own `useActivities` call, so this shares
     its cache entry rather than firing a second request for the same data. */
  const { data: activityData } = useActivities({ lead_id: lead.id, sort: '-created_at', limit: 50 });
  const openActivities = (activityData?.data ?? []).filter((a) => a.state !== 'completed' && a.state !== 'cancelled');

  return (
    <>
      <RelatedList
        title="Campaign History" icon="bi-megaphone-fill" color="var(--grad-warm)"
        count={lead.campaign ? 1 : 0}
        rows={lead.campaign ? [{ title: lead.campaign, meta: 'Attached to this lead' }] : []}
        empty="Not part of a campaign."
      />
      <RelatedList
        title="Open Activities" icon="bi-list-check" color="var(--grad-brand)"
        count={openActivities.length}
        rows={openActivities.slice(0, 5).map((a) => ({
          title: a.subject,
          meta: `${a.due_at ? `Due ${date(a.due_at)}` : a.scheduled_start ? date(a.scheduled_start) : 'No due date'} · ${a.assigned_to?.full_name ?? a.owner?.full_name ?? 'Unassigned'}`,
        }))}
        empty="No open activities."
      />
      <RelatedList
        title="Notes" icon="bi-paperclip" color="var(--grad-cool)"
        count={notes?.total ?? 0}
        rows={(notes?.data ?? []).map((n) => ({
          title: n.body?.length > 60 ? `${n.body.slice(0, 60)}…` : n.body,
          meta: `${n.author ?? 'Unknown'} · ${date(n.created_at)}`,
        }))}
        empty="No notes yet."
      />
      <RelatedList
        title="Lead History" icon="bi-clock-history" color="var(--grad-mint)"
        count={3}
        rows={[
          { title: `Status: → ${lead.status}`, meta: `${lead.owner ?? 'System'} · recently` },
          { title: `Lead Score: → ${lead.score ?? '—'}`, meta: 'System · on last scoring run' },
          { title: 'Lead created', meta: `${lead.source ?? 'Unknown source'} · ${date(lead.raw?.created_at)}` },
        ]}
        empty="No history."
      />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Right column: engagement, next step, duplicates                           */
/* -------------------------------------------------------------------------- */

export function EngagementCard({ lead }) {
  const eng = lead.engagement ?? {};
  return (
    <Card
      className="mb-3"
      title={<span className="t-sm">Engagement</span>}
      actions={<span className="pill pill-green">Active</span>}
    >
      <div className="d-flex flex-column gap-2 t-sm">
        <div className="d-flex justify-content-between"><span className="ink-3">Email opens</span><span className="fw-750 tabular">{number(eng.email_opens ?? 0)}</span></div>
        <div className="d-flex justify-content-between"><span className="ink-3">Email replies</span><span className="fw-750 tabular">{number(eng.email_replies ?? 0)}</span></div>
        <div className="d-flex justify-content-between"><span className="ink-3">Website visits</span><span className="fw-750 tabular">{number(eng.website_visits ?? 0)}</span></div>
        <div className="d-flex justify-content-between"><span className="ink-3">Meetings held</span><span className="fw-750 tabular">{number(eng.meetings_held ?? 0)}</span></div>
      </div>
    </Card>
  );
}

/** The prototype's fixed "next best action" suggestion — no recommendation engine behind it. */
export function NextStepCard() {
  return (
    <Card className="mb-3" style={{ borderColor: 'var(--brand-200)' }}
      headStyle={{ background: 'linear-gradient(120deg,var(--brand-50),#fff)' }}
      title={<span className="d-flex align-items-center gap-2"><i className="bi bi-lightning-charge-fill" aria-hidden="true" />Recommended next step</span>}
    >
      <div className="t-sm fw-650 mb-1">Book the commercial review with the decision maker</div>
      <div className="t-xs ink-3 mb-2">
        Engagement is climbing and budget signals are positive, but no meeting is scheduled with
        the economic buyer yet.
      </div>
      <div className="d-flex gap-2">
        <button type="button" className="btn btn-add btn-sm-x">Book meeting</button>
        <button type="button" className="btn btn-cancel btn-sm-x">Dismiss</button>
      </div>
    </Card>
  );
}

/** Real duplicate-detection results from `include=similar` — §4's own matcher. */
export function DuplicateCard({ lead }) {
  const matches = lead.similar ?? [];
  return (
    <Card
      title={<span className="t-sm">Duplicate check</span>}
      actions={matches.length
        ? <span className="pill pill-amber">{matches.length} match{matches.length === 1 ? '' : 'es'}</span>
        : <span className="pill pill-green">Clear</span>}
    >
      {matches.length ? matches.map((m) => (
        <div key={m.id} className="p-2 px-3 mb-2" style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)' }}>
          <div className="t-sm fw-650">{m.company_name ?? m.full_name}</div>
          <div className="t-2xs ink-3">
            {m.lead_no} · matched on {(m.match_on ?? []).join(' + ')}
            {m.confidence != null && ` · ${Math.round(m.confidence * 100)}% confidence`}
          </div>
        </div>
      )) : <div className="t-xs ink-3">No likely duplicates found for this lead.</div>}
      {matches.length > 0 && (
        <div className="t-xs ink-3">On conversion this lead may be matched to an existing account rather than creating a new one.</div>
      )}
    </Card>
  );
}

/* Activity tab moved to features/activities/ActivityTimeline.jsx — it turned
   out to have nothing lead-specific in it once the account detail page needed
   the identical composer-plus-timeline. LeadDetailPage.jsx imports it directly. */

/* -------------------------------------------------------------------------- */
/* Details tab                                                               */
/* -------------------------------------------------------------------------- */

function Fields({ children }) {
  return <div className="row g-0">{children}</div>;
}

/** One `.field-row`. Editable fields get a real `InlineCell`; the rest render flat. */
function Field({ label, children }) {
  return (
    <div className="col-md-6">
      <div className="field-row">
        <div className="fk">{label}</div>
        <div className="fv">{children}</div>
      </div>
    </div>
  );
}

/**
 * Everything the list view has no room for.
 *
 * The pencils on Status, Rating, Company, Title, Email, Phone, Industry,
 * Employees, Country, Website and Estimated Value are real — the same
 * `InlineCell` the list's table uses, PATCHing `leads.validation.js`'s
 * `updateLeadBody` field for field. Address, Qualification (BANT) and System
 * Information are read-only: `docs/API.md` §4 returns them but does not accept
 * them back on `PATCH /leads/{id}`.
 */
export function DetailsTab({ lead, options, canEdit, edits, onEdit }) {
  const addr = lead.address ?? {};
  const qual = lead.qualification ?? {};
  const sla = lead.sla ?? {};

  return (
    <>
      <div className="eyebrow mb-2">Lead Information</div>
      <Fields>
        <Field label="Lead Owner">
          {lead.owner
            ? <div className="d-flex align-items-center gap-2"><Avatar name={lead.owner} size={22} />{lead.owner}</div>
            : <span className="pill pill-amber"><i className="bi bi-inbox" aria-hidden="true" />Unassigned queue</span>}
        </Field>
        <Field label="Lead Status">
          <InlineCell
            row={lead} field="status_id" value={lead.raw?.status?.id}
            display={<span className={`pill ${STATUS_PILL[lead.status] ?? 'pill-gray'}`}>{lead.status}</span>}
            options={options.statuses} disabled={!canEdit} disabledHint="Needs leads:edit"
            edits={edits} onEdit={onEdit}
          />
        </Field>
        <Field label="Name">{lead.name}</Field>
        <Field label="Rating">
          <InlineCell
            row={lead} field="rating" value={lead.rating}
            display={<span className={`pill pill-${{ Hot: 'rose', Warm: 'amber', Cold: 'sky' }[lead.rating] ?? 'gray'}`}>{lead.rating ?? '—'}</span>}
            options={[{ value: 'Hot', label: 'Hot' }, { value: 'Warm', label: 'Warm' }, { value: 'Cold', label: 'Cold' }]}
            disabled={!canEdit} disabledHint="Needs leads:edit"
            edits={edits} onEdit={onEdit}
          />
        </Field>
        <Field label="Company">
          <InlineCell row={lead} field="company_name" value={lead.company} display={lead.company ?? '—'}
            disabled={!canEdit} disabledHint="Needs leads:edit" edits={edits} onEdit={onEdit} />
        </Field>
        <Field label="Lead Source">{lead.source ?? '—'}</Field>
        <Field label="Title">
          <InlineCell row={lead} field="job_title" value={lead.title} display={lead.title ?? '—'}
            disabled={!canEdit} disabledHint="Needs leads:edit" edits={edits} onEdit={onEdit} />
        </Field>
        <Field label="Campaign">{lead.campaign ?? '—'}</Field>
        <Field label="Email">
          <InlineCell row={lead} field="email" value={lead.email} display={lead.email ?? '—'}
            disabled={!canEdit} disabledHint="Needs leads:edit" edits={edits} onEdit={onEdit} />
        </Field>
        <Field label="Industry">
          <InlineCell row={lead} field="industry_id" value={lead.raw?.industry?.id} display={lead.industry ?? '—'}
            options={options.industries} disabled={!canEdit} disabledHint="Needs leads:edit" edits={edits} onEdit={onEdit} />
        </Field>
        <Field label="Phone">
          <InlineCell row={lead} field="phone" value={lead.phone} display={lead.phone ?? '—'}
            disabled={!canEdit} disabledHint="Needs leads:edit" edits={edits} onEdit={onEdit} />
        </Field>
        <Field label="No. of Employees">
          <InlineCell row={lead} field="employee_count" value={lead.employees} display={lead.employees ? number(lead.employees) : '—'}
            disabled={!canEdit} disabledHint="Needs leads:edit" edits={edits} onEdit={onEdit} />
        </Field>
        <Field label="Mobile">{lead.mobile ?? '—'}</Field>
        <Field label="Annual Revenue">{lead.annualRevenue ? money(lead.annualRevenue) : '—'}</Field>
        <Field label="Website">
          <InlineCell row={lead} field="website" value={lead.website} display={lead.website ?? '—'}
            disabled={!canEdit} disabledHint="Needs leads:edit" edits={edits} onEdit={onEdit} />
        </Field>
        <Field label="Est. Value">
          <InlineCell row={lead} field="estimated_value" value={lead.value} display={lead.value ? money(lead.value, lead.currency) : '—'}
            disabled={!canEdit} disabledHint="Needs leads:edit" edits={edits} onEdit={onEdit} />
        </Field>
      </Fields>

      <hr className="soft" />
      <div className="eyebrow mb-2">Address Information</div>
      <Fields>
        <Field label="Street">{[addr.line1, addr.line2].filter(Boolean).join(', ') || '—'}</Field>
        <Field label="Country">{lead.country ?? addr.country ?? '—'}</Field>
        <Field label="City">{addr.city ?? '—'}</Field>
        <Field label="State/Province">{addr.state ?? '—'}</Field>
        <Field label="Postal Code">{addr.postal_code ?? '—'}</Field>
      </Fields>

      <hr className="soft" />
      <div className="eyebrow mb-2">Qualification (BANT)</div>
      <Fields>
        <Field label="Budget">
          {qual.has_budget == null
            ? '—'
            : qual.has_budget
              ? <span className="pill pill-green"><i className="bi bi-check-lg" aria-hidden="true" />Confirmed</span>
              : <span className="pill pill-amber">Not confirmed</span>}
        </Field>
        <Field label="Authority">{qual.has_authority == null ? '—' : (qual.has_authority ? 'Confirmed decision maker' : 'Not confirmed')}</Field>
        <Field label="Need">{qual.requirement_summary || '—'}</Field>
        <Field label="Timeline">
          {qual.timeline_confirmed == null
            ? '—'
            : qual.timeline_confirmed
              ? <span className="pill pill-green">Confirmed</span>
              : <span className="pill pill-amber">Not confirmed</span>}
        </Field>
      </Fields>

      <hr className="soft" />
      <div className="eyebrow mb-2">System Information</div>
      <Fields>
        <Field label="Lead Number">{lead.lead_no}</Field>
        <Field label="Last Activity">{lead.raw?.updated_at ? relativeTime(lead.raw.updated_at) : '—'}</Field>
        <Field label="First Response Due">{sla.first_response_due_at ? dateTime(sla.first_response_due_at) : '—'}</Field>
        <Field label="SLA Met">
          {sla.met == null ? <span className="pill pill-gray">Pending</span>
            : sla.met ? <span className="pill pill-green">Yes</span> : <span className="pill pill-rose">Missed</span>}
        </Field>
      </Fields>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* News — decorative, matching the prototype's own fixed content             */
/* -------------------------------------------------------------------------- */

export function NewsTab({ company }) {
  return (
    <EmptyState
      icon="bi-newspaper"
      title="No news source connected"
      message={`Company news for ${company ?? 'this account'} would appear here once a provider is configured.`}
    />
  );
}
